import { type ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";
import { useAppSelector } from "@/store";
import { selectIsOnboarded } from "@/store/selectors";
import { useOnboardingBootstrap } from "@/hooks/useOnboardingBootstrap";
import { Loader2, Sparkles } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requireOnboarding?: boolean;
}

export function ProtectedRoute({ children, requireOnboarding = true }: ProtectedRouteProps) {
  const { initialized, isAuthenticated, login, initError } = useAuth();
  const isOnboarded = useAppSelector(selectIsOnboarded);
  const { t } = useTranslation();
  const location = useLocation();

  // Bootstrap deve girare PRIMA di qualsiasi early return (regola degli
  // hooks). Internamente è gated da `isAuthenticated` via react-query
  // `enabled`. Va invocato qui — non in `ChatLayout` — perché la route
  // `/onboarding` non passa da quel layout: senza bootstrap qui, il
  // wizard prova a salvare con `activeRole` undefined → 400 dal BE.
  const { isBootstrapping } = useOnboardingBootstrap();

  useEffect(() => {
    if (initialized && !isAuthenticated && !initError) {
      login();
    }
  }, [initialized, isAuthenticated, initError, login]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-muted gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <span className="text-sm">{t("common.loading")}</span>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-strong rounded-card max-w-md p-8 text-center">
          <div className="inline-flex items-center gap-2 chip mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Auth offline
          </div>
          <h2 className="text-2xl mb-2">{t("auth.loginTitle")}</h2>
          <p className="text-muted mb-4">{initError.message}</p>
          <p className="text-xs text-muted">
            Verifica che Keycloak sia raggiungibile a <code>{import.meta.env.VITE_KEYCLOAK_URL}</code>.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted">
        <span className="text-sm">{t("auth.loggingIn")}</span>
      </div>
    );
  }

  // Senza questo gating Redux è in initialState dopo il purge al logout
  // (keycloak.ts) e `isOnboarded` risulta sempre false finché meQuery non
  // risponde → l'utente viene navigato al wizard anche se già onboardato.
  if (requireOnboarding && isBootstrapping && location.pathname !== "/onboarding") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-muted gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <span className="text-sm">{t("common.loading")}</span>
      </div>
    );
  }

  if (requireOnboarding && !isOnboarded && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
